/**
 * useDietaryConflictMap — shared hook for ALL menu layouts.
 *
 * Returns a Map keyed by item id whose values describe the conflict
 * with the diner's saved allergen card. Use it like:
 *
 *   const conflictMap = useDietaryConflictMap(items);
 *   const conflict    = conflictMap.get(item.id);
 *   conflict.hasAllergen   → red overlay + lock Add button
 *   conflict.missingDiet   → preference miss (greyed)
 *   conflict.reasons       → tooltip strings
 *
 * Why a hook + Map rather than per-item .filter? So we can keep
 * non-matching items in the rendered list (greyed) instead of
 * hiding them. That's a stated UX requirement: diners SEE what
 * exists and understand why it's not for them.
 */
import { useMemo } from "react";
import { useDietaryProfile } from "@/context/DietaryProfileContext";
import { checkConflicts } from "@/lib/dietaryConflict";


export function useDietaryConflictMap(items) {
  const { profile } = useDietaryProfile();
  return useMemo(() => {
    const m = new Map();
    for (const it of (items || [])) {
      m.set(it.id, checkConflicts(it, profile));
    }
    return m;
  }, [items, profile]);
}


/**
 * AllergenBadge — drops a red "⚠ peanuts, dairy" pill onto any
 * conflicting menu item. Designed for absolute positioning over
 * the card image / next to the title.
 */
export function AllergenBadge({ conflict, testId, size = "sm" }) {
  if (!conflict?.hasAllergen) return null;
  const sm = size === "sm";
  return (
    <span data-testid={testId}
              className={`bg-rose-700 text-white font-bold rounded-full
                              ${sm ? "text-[10px] px-1.5 py-0.5"
                                  : "text-xs px-2.5 py-1"}`}
              title={conflict.reasons.join(" · ")}>
      ⚠ {conflict.allergens.join(", ")}
    </span>
  );
}


/** Same idea but full-width badge for list rows. */
export function BlockedAddButton({ conflict, testId, fullSize }) {
  if (!conflict?.hasAllergen) return null;
  return (
    <button type="button" disabled data-testid={testId}
              title={conflict.reasons.join(" · ")}
              className={`bg-rose-200 text-rose-800 rounded-full
                                ${fullSize ? "h-9 px-4 text-sm"
                                              : "h-8 px-3 text-xs"}
                                font-semibold cursor-not-allowed`}>
      ⚠ Blocked
    </button>
  );
}


/** CSS class snippet to apply on the card wrapper. */
export function conflictWrapperClass(conflict) {
  if (conflict?.hasAllergen) return "opacity-50 grayscale";
  if (conflict?.missingDiet) return "opacity-75";
  return "";
}
